import { makeStyles } from '@focaldata/cin-ui-components'
import { Link, useNavigate } from 'react-router-dom'

export interface ProjectBreadcrumbStep {
  label: string
  // Steps without a path render as plain labels rather than links.
  path?: string
}

const useStyles = makeStyles()({
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
    padding: '12px 24px',
    borderBottom: '1px solid #E5EAF5',
    background: '#FFFFFF'
  },
  allProjects: {
    textDecoration: 'none',
    border: '1px solid #E5EAF5',
    borderRadius: 8,
    padding: '4px 10px',
    color: '#42526E',
    fontSize: 14,
    marginRight: 8
  },
  // reset so a native (accessible) <button> can be styled as a breadcrumb step
  stepButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer'
  },
  step: {
    padding: '4px 10px',
    borderRadius: 6,
    color: '#8993A4',
    fontSize: 14
  },
  stepActive: {
    color: 'var(--primary)',
    backgroundColor: '#EAF1FF',
    fontWeight: 600
  },
  sep: { color: '#B3BAC5' }
})

/** Presentational: callers decide the steps and which of them are navigable. */
export const ProjectBreadcrumb = ({
  steps,
  activeStep,
  backLabel = 'All Projects',
  backTo = '/projects',
  hideBack = false
}: {
  steps: ProjectBreadcrumbStep[]
  activeStep: string
  // A nested flow goes back to its parent: a wave's steps sit under a tracker.
  backLabel?: string
  backTo?: string
  hideBack?: boolean
}) => {
  const { classes, cx } = useStyles()
  const navigate = useNavigate()

  return (
    <nav className={classes.breadcrumb}>
      {!hideBack && (
        <Link to={backTo} className={classes.allProjects}>
          ← {backLabel}
        </Link>
      )}
      {steps.map(({ label, path }, index) => {
        const isActive = label === activeStep
        const stepLabel = (
          <span className={cx(classes.step, isActive && classes.stepActive)}>
            {label}
          </span>
        )
        return (
          <span
            key={label}
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            {index > 0 && <span className={classes.sep}>›</span>}
            {path && !isActive ? (
              <button
                type="button"
                className={classes.stepButton}
                onClick={() => navigate(path)}
              >
                {stepLabel}
              </button>
            ) : (
              stepLabel
            )}
          </span>
        )
      })}
    </nav>
  )
}
